import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";

export interface UsageData {
  plan: {
    type: "free" | "pro" | "max";
  };
  usage: {
    current: number;
    limit: number;
    remaining: number;
    resetIn: number | null;
  };
}

interface UsageState {
  data: UsageData | null;
  isLoading: boolean;
  error: string | null;
}

const API_BASE_URL = "/api/v1";

export const useUsage = () => {
  const { getToken, isLoaded } = useAuth();
  const [state, setState] = useState<UsageState>({
    data: null,
    isLoading: true,
    error: null,
  });
  const hasFetchedRef = useRef(false);

  const fetchUsage = useCallback(async () => {
    if (!isLoaded) return;

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const token = await getToken();
      if (!token) {
        throw new Error("Authentication token not available");
      }

      const response = await fetch(`${API_BASE_URL}/auth/usage`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch usage data");
      }

      const data = await response.json();
      setState({ data, isLoading: false, error: null });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load usage data";
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      console.error("Error fetching usage data:", err);
    }
  }, [getToken, isLoaded]);

  // Only fetch once when authentication state is ready, prevent duplicate calls
  useEffect(() => {
    if (isLoaded && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchUsage();
    }
  }, [isLoaded, fetchUsage]);

  return {
    usage: state.data,
    isLoading: state.isLoading,
    error: state.error,
    refetch: useCallback(() => {
      hasFetchedRef.current = false; // Reset flag to allow refetch
      fetchUsage();
    }, [fetchUsage]),
  };
};
